import { CommonModule, registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import localeFrExtra from '@angular/common/locales/extra/he';
import localeHe from '@angular/common/locales/he';
import { DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { EditorModule } from '@tinymce/tinymce-angular';
import { he } from 'date-fns/locale';
import { DateFnsConfigurationService, DateFnsModule } from 'ngx-date-fns';
import { NgxEchartsModule } from 'ngx-echarts';
import { NgPipesModule } from 'ngx-pipes';
import { BaseCertificateDetailsComponent } from './base-certificate-details';
import { BidComponent } from './bid/bid.component';
import { CertificateEndLineComponent } from './certificate-end-line';
import { CertificateFooterComponent } from './certificate-footer/certificate-footer.component';
import { CertificateSignatureComponent } from './certificate-signature/certificate-signature.component';
import { CertificateSignaturesComponent } from './certificate-signatures/certificate-signatures.component';
import {
  CertificateFireAlarmSystemComponent,
  CertificateFireExtinguishersComponent,
  CertificatePressureSystemInStairwellsCompatibilityCheckComponent,
  CertificateVentilationSystemAndFireProtectionComponent,
  FireAlarmSystemTestResultsComponent,
  FireExtinguishersTestResultsComponent,
  PressureSystemInStairwellsCompatibilityCheckTestResultsComponent,
  VentilationSystemAndFireProtectionTestResultsComponent
} from './certificates';
import {
  AerosolShutdownTestResultsComponent,
  CertificateAerosolShutdownComponent
} from './certificates/aerosol-shutdown';
import { AirConditioningAndVentilationSystemsTestResultsComponent } from './certificates/air-conditioning-and-ventilation-systems';
import { CertificateAirConditioningAndVentilationSystemsComponent } from './certificates/air-conditioning-and-ventilation-systems/certificate-air-conditioning-and-ventilation-systems/certificate-air-conditioning-and-ventilation-systems.component';
import {
  CertificateFireTapsComponent,
  FireTapsTestResultsComponent
} from './certificates/fire-taps';
import { CertificateGasExtinguishingSystemComponent } from './certificates/gas-extinguishing-system/certificate-gas-extinguishing-system/certificate-gas-extinguishing-system.component';
import { GasExtinguishingSystemTestResultsComponent } from './certificates/gas-extinguishing-system/gas-extinguishing-system-test-results/gas-extinguishing-system-test-results.component';
import { CertificateIntegrationComponent } from './certificates/integration/certificate-integration/certificate-integration.component';
import { IntegrationTestResultsComponent } from './certificates/integration/integration-test-results/integration-test-results.component';
import { CertificateInstallationComponent } from './certificates/sprinklers/certificate-installation/certificate-installation.component';
import { CertificatePlanningComponent } from './certificates/sprinklers/certificate-planning/certificate-planning.component';
import { CertificateComponent } from './certificates/sprinklers/certificate-sprinklers/certificate.component';
import { CertificateWaterNetworkCharacterizationComponent } from './certificates/water-network/certificate-water-network-characterization/certificate-water-network-characterization.component';
import { CertificateWaterNetworkComponent } from './certificates/water-network/certificate-water-network/certificate-water-network.component';
import { DocTemplateComponent } from './doc-template/doc-template.component';
import { FailedInspectionNotesComponent } from './failed-inspection-notes';
import { PageBreakComponent } from './page-break/page-break.component';
import { PdfViewerComponent } from './pdf-viewer/pdf-viewer.component';
import { SafePipe } from './pipes/safe/safe.pipe';
import { SummaryNotesComponent } from './summary-notes/summary-notes.component';
import { WaterSupplyChartComponent } from './water-supply-chart/water-supply-chart.component';

registerLocaleData(localeHe, 'he', localeFrExtra);
const fnsConfig = new DateFnsConfigurationService();
fnsConfig.setLocale(he);

@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    FormsModule,
    MatIconModule,
    DateFnsModule.forRoot(),
    EditorModule,
    NgPipesModule,
    NgxEchartsModule.forRoot({
      /**
       * This will import all modules from echarts.
       * If you only need custom modules,
       * please refer to [Custom Build] section.
       */
      echarts: () => import('echarts') // or import('./path-to-my-custom-echarts')
    })
  ],
  declarations: [
    BidComponent,
    DocTemplateComponent,
    PageBreakComponent,
    CertificateEndLineComponent,
    SummaryNotesComponent,
    BaseCertificateDetailsComponent,
    CertificateComponent,
    CertificatePlanningComponent,
    CertificateSignatureComponent,
    CertificateSignaturesComponent,
    CertificateInstallationComponent,
    CertificateWaterNetworkComponent,
    CertificateWaterNetworkCharacterizationComponent,
    SafePipe,
    FailedInspectionNotesComponent,
    WaterSupplyChartComponent,
    CertificateFooterComponent,

    CertificateGasExtinguishingSystemComponent,
    GasExtinguishingSystemTestResultsComponent,

    CertificateFireTapsComponent,
    FireTapsTestResultsComponent,

    CertificateFireExtinguishersComponent,
    FireExtinguishersTestResultsComponent,

    CertificateAerosolShutdownComponent,
    AerosolShutdownTestResultsComponent,

    CertificateFireAlarmSystemComponent,
    FireAlarmSystemTestResultsComponent,

    CertificateAirConditioningAndVentilationSystemsComponent,
    AirConditioningAndVentilationSystemsTestResultsComponent,

    CertificateVentilationSystemAndFireProtectionComponent,
    VentilationSystemAndFireProtectionTestResultsComponent,

    CertificatePressureSystemInStairwellsCompatibilityCheckComponent,
    PressureSystemInStairwellsCompatibilityCheckTestResultsComponent,

    CertificateIntegrationComponent,
    IntegrationTestResultsComponent,

    PdfViewerComponent
  ],
  exports: [
    BidComponent,
    DocTemplateComponent,
    PageBreakComponent,
    FailedInspectionNotesComponent,
    CertificateEndLineComponent,
    SummaryNotesComponent,
    CertificateComponent,
    CertificateWaterNetworkComponent,
    SafePipe,
    WaterSupplyChartComponent,
    CertificateGasExtinguishingSystemComponent,
    CertificateFireTapsComponent,
    CertificateFireExtinguishersComponent,
    CertificateAerosolShutdownComponent,
    CertificateFireAlarmSystemComponent,
    CertificateAirConditioningAndVentilationSystemsComponent,
    CertificateVentilationSystemAndFireProtectionComponent,
    CertificatePressureSystemInStairwellsCompatibilityCheckComponent,
    CertificateIntegrationComponent,
    PdfViewerComponent
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'he' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'ILS' },
    { provide: DateFnsConfigurationService, useValue: fnsConfig }
  ]
})
export class UiModule {}
